import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Settings.css';
import CategoryManagement from './CategoryManagement';
import TelegramSettings from './TelegramSettings';

function Settings() {
  const navigate = useNavigate();
  
  // User Profile State
  const [userProfile, setUserProfile] = useState({
    email: '',
    name: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Budget Settings State
  const [budgetSettings, setBudgetSettings] = useState({
    needs_percentage: 60,
    wants_percentage: 30,
    savings_percentage: 10
  });
  
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [budgetSuccess, setBudgetSuccess] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [budgetError, setBudgetError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile
      try {
        const profileResponse = await api.get('/auth/profile');
        setUserProfile(profileResponse.data);
      } catch (error) {
        console.log('Profile endpoint error:', error);
      }
      
      // Fetch budget settings
      const budgetResponse = await api.get('/settings/budget');
      setBudgetSettings(budgetResponse.data);
      
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // User Profile Handlers
  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
    setProfileError(null);
    setProfileSuccess(false);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setProfileError(null);
      
      await api.put('/auth/profile', {
        email: userProfile.email,
        name: userProfile.name
      });
      
      // Update localStorage and notify other components (like Sidebar)
      localStorage.setItem('userName', userProfile.name || userProfile.email);
      window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
        detail: { name: userProfile.name || userProfile.email } 
      }));
      
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setProfileError(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Password Handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      setSavingPassword(true);
      setPasswordError(null);
      
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  // Budget Settings Handlers
  const handleBudgetChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setBudgetSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
    setBudgetError(null);
    setBudgetSuccess(false);
  };

  const getBudgetTotal = () => {
    return budgetSettings.needs_percentage + budgetSettings.wants_percentage + budgetSettings.savings_percentage;
  };

  const handleSaveBudget = async () => {
    const total = getBudgetTotal();
    if (total !== 100) {
      setBudgetError(`Percentages must add up to 100% (currently: ${total}%)`);
      return;
    }

    try {
      setSavingBudget(true);
      setBudgetError(null);
      
      const response = await api.put('/settings/budget', budgetSettings);
      setBudgetSettings(response.data);
      setBudgetSuccess(true);
      
      setTimeout(() => setBudgetSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving budget settings:', error);
      setBudgetError(`Failed to save budget settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSavingBudget(false);
    }
  };

  const applyPreset = (needs, wants, savings) => {
    setBudgetSettings({
      needs_percentage: needs,
      wants_percentage: wants,
      savings_percentage: savings
    });
    setBudgetError(null);
    setBudgetSuccess(false);
  };

  const budgetTotal = getBudgetTotal();
  const isBudgetValid = budgetTotal === 100;

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>⚙️ Settings</h1>
      </div>

      <div className="settings-content">
        
        {/* User Profile Section */}
        <div className="settings-section">
          <h2>👤 User Profile</h2>
          
          {profileSuccess && (
            <div className="success-banner">
              ✓ Profile updated successfully!
            </div>
          )}

          <div className="settings-card">
            <div className="form-group">
              <label htmlFor="name">Display Name</label>
              <input
                type="text"
                id="name"
                value={userProfile.name || ''}
                onChange={(e) => handleProfileChange('name', e.target.value)}
                placeholder="Enter your display name"
                className="form-input"
              />
              <p className="input-hint">This name will be shown in the welcome message and to other household members.</p>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={userProfile.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                placeholder="Enter your email"
                className="form-input"
              />
            </div>

            {profileError && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                {profileError}
              </div>
            )}

            <button
              className="save-btn"
              onClick={handleSaveProfile}
              disabled={savingProfile}
            >
              {savingProfile ? 'Saving...' : '💾 Save Profile'}
            </button>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="settings-section">
          <h2>🔒 Change Password</h2>
          
          {passwordSuccess && (
            <div className="success-banner">
              ✓ Password changed successfully!
            </div>
          )}

          <div className="settings-card">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Enter current password"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Enter new password"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirm new password"
                className="form-input"
              />
            </div>

            {passwordError && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                {passwordError}
              </div>
            )}

            <button
              className="save-btn"
              onClick={handleChangePassword}
              disabled={savingPassword || !passwordData.currentPassword || !passwordData.newPassword}
            >
              {savingPassword ? 'Changing...' : '🔑 Change Password'}
            </button>
          </div>
        </div>

        {/* Budget Rule Preferences Section */}
        <div className="settings-section">
          <h2>📊 Budget Rule Preferences</h2>
          
          {budgetSuccess && (
            <div className="success-banner">
              ✓ Budget settings saved successfully!
            </div>
          )}

          <div className="settings-card">
            <p className="settings-description">
              Customize your ideal budget allocation percentages. They must add up to 100%.
            </p>

            <div className="budget-settings">
              <div className="budget-item needs">
                <div className="budget-header">
                  <label>🏠 Essential Expenses</label>
                  <span className="percentage-value">{budgetSettings.needs_percentage}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={budgetSettings.needs_percentage}
                  onChange={(e) => handleBudgetChange('needs_percentage', e.target.value)}
                  className="slider needs-slider"
                />
                <p className="budget-description">Housing, food, utilities, transportation, healthcare</p>
              </div>

              <div className="budget-item wants">
                <div className="budget-header">
                  <label>🎉 Discretionary Spending</label>
                  <span className="percentage-value">{budgetSettings.wants_percentage}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={budgetSettings.wants_percentage}
                  onChange={(e) => handleBudgetChange('wants_percentage', e.target.value)}
                  className="slider wants-slider"
                />
                <p className="budget-description">Entertainment, shopping, dining out, subscriptions</p>
              </div>

              <div className="budget-item savings">
                <div className="budget-header">
                  <label>💰 Savings & Investments</label>
                  <span className="percentage-value">{budgetSettings.savings_percentage}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={budgetSettings.savings_percentage}
                  onChange={(e) => handleBudgetChange('savings_percentage', e.target.value)}
                  className="slider savings-slider"
                />
                <p className="budget-description">Emergency fund, investments, debt repayment</p>
              </div>
            </div>

            <div className={`total-display ${isBudgetValid ? 'valid' : 'invalid'}`}>
              <span className="total-label">Total:</span>
              <span className="total-percentage">{budgetTotal}% / 100%</span>
            </div>

            {budgetError && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                {budgetError}
              </div>
            )}

            <div className="quick-presets">
              <h3>Quick Presets:</h3>
              <div className="preset-buttons">
                <button className="preset-btn" onClick={() => applyPreset(60, 30, 10)}>
                  60/30/10 (Standard)
                </button>
                <button className="preset-btn" onClick={() => applyPreset(50, 30, 20)}>
                  50/30/20 (Aggressive Saving)
                </button>
                <button className="preset-btn" onClick={() => applyPreset(70, 25, 5)}>
                  70/25/5 (Tight Budget)
                </button>
                <button className="preset-btn" onClick={() => applyPreset(50, 20, 30)}>
                  50/20/30 (FIRE Strategy)
                </button>
              </div>
            </div>

            <button
              className="save-btn"
              onClick={handleSaveBudget}
              disabled={!isBudgetValid || savingBudget}
            >
              {savingBudget ? 'Saving...' : '💾 Save Budget Settings'}
            </button>
          </div>
        </div>

        {/* Telegram Bot Section */}
        <div className="settings-section">
          <TelegramSettings />
        </div>

        {/* Category Management Section */}
        <CategoryManagement />

      </div>
    </div>
  );
}

export default Settings;
